import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { MessageCircle, X, Send, Bot, Phone } from "lucide-react";
import api from "@/lib/api";

let _msgId = 0;
const nextMsgId = () => ++_msgId;

export default function CustomerAssistant({ slug, businessName, whatsapp, orderId }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { id: 0, role: "ai", text: t("assistant.greeting", { name: businessName || t("assistant.theBoutique") }) },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [wa, setWa] = useState(whatsapp || "");
  const endRef = useRef(null);

  useEffect(() => { if (open) endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const send = async () => {
    if (!input.trim() || busy) return;
    const msg = input;
    setMessages((m) => [...m, { id: nextMsgId(), role: "user", text: msg }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post(`/public/store/${slug}/assistant`, { message: msg, order_id: orderId });
      setMessages((m) => [...m, { id: nextMsgId(), role: "ai", text: data.reply }]);
      if (data.whatsapp) setWa(data.whatsapp);
    } catch (err) {
      const status = err?.response?.status;
      const text = status === 429
        ? t("assistant.rateLimit")
        : t("assistant.unavailable");
      setMessages((m) => [...m, { id: nextMsgId(), role: "ai", text }]);
    } finally {
      setBusy(false);
    }
  };

  const waLink = wa ? `https://wa.me/${wa.replace(/\D/g, "")}` : "";

  return (
    <>
      {!open && (
        <button
          data-testid="assistant-open-btn"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-4 z-50 h-14 w-14 rounded-full bg-[#1B4332] text-white shadow-xl flex items-center justify-center active:scale-95 transition-transform"
          aria-label="Assistant"
        >
          <MessageCircle className="h-7 w-7" />
          <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-[#E85D04] border-2 border-white" />
        </button>
      )}

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            data-testid="assistant-panel"
            className="fixed bottom-0 right-0 left-0 sm:left-auto sm:bottom-5 sm:right-4 z-50 sm:w-96 max-w-lg mx-auto"
          >
            <div className="bg-white sm:rounded-3xl rounded-t-3xl shadow-2xl border border-black/5 flex flex-col h-[70vh] sm:h-[34rem] overflow-hidden">
              <div className="bg-[#1B4332] text-white px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="h-9 w-9 rounded-full bg-white/15 flex items-center justify-center"><Bot className="h-5 w-5" /></span>
                  <div>
                    <p className="font-display font-bold text-sm leading-tight">{t("assistant.title", { name: businessName })}</p>
                    <p className="text-[10px] text-white/70">{t("assistant.online")}</p>
                  </div>
                </div>
                <button data-testid="assistant-close-btn" onClick={() => setOpen(false)} className="h-8 w-8 rounded-full bg-white/15 flex items-center justify-center"><X className="h-4 w-4" /></button>
              </div>

              <div className="flex-1 overflow-y-auto no-scrollbar p-3 space-y-3 bg-[#F9F6F0]">
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    {m.role === "ai" && <span className="h-7 w-7 rounded-lg bg-[#E85D04]/10 flex items-center justify-center mr-2 shrink-0 self-end"><Bot className="h-4 w-4 text-[#E85D04]" /></span>}
                    <div data-testid={`assistant-msg-${m.role}`} className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${m.role === "user" ? "bg-[#1B4332] text-white rounded-br-sm" : "bg-white border border-black/5 rounded-bl-sm"}`}>
                      {m.text}
                    </div>
                  </div>
                ))}
                {busy && <div className="flex items-center gap-1 text-[#9c968a] text-xs ml-9"><span className="h-2 w-2 rounded-full bg-[#E85D04] animate-bounce" /> {t("assistant.writing")}</div>}
                <div ref={endRef} />
              </div>

              {waLink && (
                <a href={waLink} target="_blank" rel="noreferrer" data-testid="assistant-whatsapp-btn" className="mx-3 mb-2 h-10 rounded-xl bg-[#25D366] text-white text-sm font-semibold flex items-center justify-center gap-2">
                  <Phone className="h-4 w-4" /> {t("assistant.talkToSeller")}
                </a>
              )}

              <div className="flex items-center gap-2 p-3 border-t border-[#E5E0D8]">
                <input
                  data-testid="assistant-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder={t("assistant.yourQuestion")}
                  className="flex-1 h-11 bg-[#F9F6F0] rounded-2xl px-4 outline-none border border-[#E5E0D8] focus:border-[#E85D04] text-sm"
                />
                <button data-testid="assistant-send-btn" onClick={send} disabled={busy} className="h-11 w-11 rounded-2xl bg-[#E85D04] text-white flex items-center justify-center active:scale-95 transition-transform disabled:opacity-60">
                  <Send className="h-5 w-5" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
